import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Table, Button, Space, message, Modal } from 'antd';
import { ExportOutlined, DeleteOutlined, FolderOutlined } from '@ant-design/icons';
import { getViewsFolder, exportFiles, getViewByProjectId } from '../../api/data-project-view';
import { deleteFolder } from '../../api/data-project-folder';
import FolderContentTable from './components/FolderContentTable';
import ExportConfirmModal from './components/ExportConfirmModal';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './index.module.less';
import { ReactComponent as ZlbzFolderIcon } from '@/assets/svg/zlbz-folder.svg'
import { getProjectById, } from '../../api/data-project';
import { reviewDataProject } from '@/api/cloudProject';
import useStore, { FolderItem } from '../../store/useStore';
import { useWindowSize } from 'react-use'
import useScreenHeight from '@/hooks/useScreenHeight';
import { margin } from '@/config/theme'
import useMethods from '../../onlyofficeFieldSetting/hooks/usemethods';
import { useShallow } from 'zustand/react/shallow';
import { useAuth } from '@/hooks/useAuth';
import { LockCodeEnum, LockConfig } from '@/config/constant';
import { ResCodeEnum } from '@/utils/request';
import usePollingWithIdleCheck from '@/common/hooks/usePollingWithIdleCheck';
import { getDocLock, } from '../../api/data-lock';
import { releaseDocumentLock, } from '@/utils/lock';
import { DocStatusEnum } from '../../dict/type';

interface DetailProps {
  projectId?: number;
}

const Detail: React.FC<DetailProps> = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const name = searchParams.get('name');
  const detailReferrer = searchParams.get('detailReferrer');
  const requestId = searchParams.get('requestId');
  const editable = searchParams.get('editable');
  const isEditable = editable === '1'
  const editRef = useRef(isEditable)
  const alertRef = useRef(false)
  const { hasAuth, codes } = useAuth()
  const authEdit = useMemo(() => hasAuth('630:doc:prep:proj:modify'), [codes])

  // 使用 store 中的状态和方法
  const { folderList, folderListLoading, deatailEditabled, projectDetail } = useStore(
    useShallow((s) => ({
      folderList: s.folderList,
      folderListLoading: s.folderListLoading,
      deatailEditabled: s.deatailEditabled,
      projectDetail: s.projectDetail
    }))
  );

  const setStoreState = useStore.setState;

  const [currentView, setCurrentView] = useState<'volume' | 'document'>('volume');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [currentFolder, setCurrentFolder] = useState<FolderItem | null>(null);
  const [exportModalVisible, setExportModalVisible] = useState(false);

  const { handleExport } = useMethods();

  // 获取文件夹列表
  const fetchFolderList = async () => {
    try {
      setStoreState({ folderListLoading: true });
      const promises = [getViewsFolder(projectId), getProjectById(projectId), getViewByProjectId(projectId)]
      const [result, projectInfo, viewInfo] = await Promise.all(promises)
      // result 根据 viewId  补充 catalogDocUrl 
      const newRsutl = (result as any[]).map(i => {
        const trueViewInfo = (viewInfo as any[]).find(j => j.id === i.viewId)
        return {
          ...i,
          catalogDocUrl: trueViewInfo?.catalogDocUrl
        }
      })
      setStoreState({
        folderList: newRsutl as any[] || [],
        projectDetail: projectInfo,
        settingProjectTitle: (projectInfo as any)?.projectName as any,
      })
    } catch (error) {
      message.error('获取文件夹列表失败');
    } finally {
      setStoreState({ folderListLoading: false });
    }
  };
  // 第一个表格列定义（目录表格）
  const folderColumns: ColumnsType<FolderItem> = [
    {
      title: '资料目录/名称',
      dataIndex: 'folderName',
      key: 'folderName',
      render: (text: string, record: FolderItem) => (
        <Space style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', margin: 0 }}><ZlbzFolderIcon /></div>
          <a
            onClick={() => handleFolderClick(record)}
            style={{ color: '#1890ff' }}
          >
            {text}
          </a>
        </Space>
      ),
    },
    {
      title: '文件个数',
      dataIndex: 'fileCount',
      key: 'fileCount',
      render: (count: number) => `${count}个`,
    },
    {
      title: '文件大小',
      dataIndex: 'fileSize',
      key: 'fileSize',
      render: (size: number) => {
        if (size < 1024) return `${size}B`;
        if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`;
        return `${(size / (1024 * 1024)).toFixed(1)}MB`;
      },
    },
    {
      title: '文件类型',
      dataIndex: 'fileType',
      key: 'fileType',
      render: () => '文件夹',
    },
  ];

  // 处理卷目录点击
  const handleFolderClick = (folder: FolderItem) => {
    setCurrentFolder(folder);
    localStorage.setItem('currentViewId', folder.viewId.toString());
    setCurrentView('document');
    setStoreState({
      currentDetailFolder: folder,
      currentSettingDetailFolderDoc: { ...folder },
      parentFolderId: folder.folderId
    })
  };

  // 返回卷目录列表
  const handleBackToFolder = () => {
    setCurrentView('volume');
    setCurrentFolder(null);
    setSelectedRowKeys([]);
    // 刷新文件夹列表
    fetchFolderList();
  };

  // 导出选中项 - 打开确认弹窗
  const handleExportSelected = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要导出的项目');
      return;
    }
    setExportModalVisible(true);
  };

  // 确认导出
  const handleExportConfirm = async (convertToPdf: boolean) => {
    const params = {
      convertToPdf,
      folderIds: selectedRowKeys,
      projectId: projectId
    };

    try {
      setExportLoading(true);
      await handleExport(params);
      setExportModalVisible(false);
    } catch (error) {
      // 错误已在 useMethods 中处理
      console.error('导出失败:', error);
    } finally {
      setExportLoading(false);
    }
  };

  // 取消导出
  const handleExportCancel = () => {
    setExportModalVisible(false);
  };

  // 批量删除文件夹
  const handleBatchDeleteFolder = () => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 项吗？`,
      onOk: async () => {
        try {
          await deleteFolder({
            projectId,
            "folderIds": selectedRowKeys as number[]
          }, requestId);
          message.success('删除成功');
          setSelectedRowKeys([]);
          fetchFolderList();
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  // 审阅通过
  const handleReviewProject = () => {
    Modal.confirm({
      title: '确认操作',
      content: '即将审阅通过，资料状态变为"已完成"！此操作不可撤销，是否确认操作？',
      onOk: async () => {
        try {
          await reviewDataProject({
            projectId,
          });
          message.success('审阅成功');
          fetchFolderList(); // 刷新列表以更新状态
        } catch (error) {
          message.error('审阅失败');
        }
      },
    });
  };

  // 行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => {
      setSelectedRowKeys(keys);
    },
  };


  const { height: screenHeight, innerHeight } = useScreenHeight()
  // const otherHeight = useMemo(() => {
  //   // 64: 顶部搜索栏高度 16: 外padding 16: 内部padding 16 操作栏高度 40
  //   return 16 + 16 + 40
  // }, [])
  // const containerStyle: React.CSSProperties = {
  //   height: `${screenHeight}px`
  // }

  const windowSize = useWindowSize()
  const tableWrapRef = useRef(null)
  const [srcollHeight, setSrcollHeight] = useState(300)

  const clientTop = (tableWrapRef.current as any)?.getBoundingClientRect()?.top
  // console.log("clientTop", clientTop)

  // 指标锁失败提示框
  const lockFailConfirm = (title: string) => {
    if (alertRef.current) {
      return
    }
    Modal.confirm({
      title: `用户「${title}」正在编辑此工程，您将以只读模式查看。确认继续吗？`,
      okText: '确认',
      cancelButtonProps: { style: { display: 'none' } },
      onOk() {
        setStoreState({ isEditMode: false });
        // 
        const fullUrl = window.location.href
        const hashPart = window.location.hash || fullUrl.split('#')[1];
        const newUrl = fullUrl.replaceAll('editable=1', 'editable=0');
        // const newUrl = `/document/engList/detail?projectId=${projectId}&requestId=${requestId}&editable=0`
        // console.log(newHash)
        // navigate(newUrl)
        window.location.href = newUrl
        // navigate(newUrl)
        // 更新编辑器配置为查看模式
        // const latestEditorConfig = useStore.getState().editorConfigBack;
        // if (latestEditorConfig) {
        //   const newConfig = {
        //     ...latestEditorConfig,
        //     editorConfig: {
        //       ...latestEditorConfig.editorConfig,
        //       mode: 'view'
        //     }
        //   };
        //   setStoreState({
        //     editorConfig: { ...newConfig },
        //     editorConfigBack: { ...newConfig }
        //   });
        // }
      },
      onCancel() { },
    });
    alertRef.current = true

  }

  // 锁检查函数
  const checkLock = async () => {
    if (!projectId) return;
    if (!editRef.current) return;
    const params = {
      code: LockCodeEnum.PROJECT,
      resourceId: projectId,
      timeToLive: LockConfig.editLockTime,
      requestId: requestId
    }

    try {
      const res = await getDocLock(params);
      if (res?.code === ResCodeEnum.RequestResourceLocked) {
        const match = res.msg?.match(/\[(.*?)\]/);
        const title = match?.[1]?.split('_')[0] || '未知用户';
        lockFailConfirm(title)
      } else {
        Modal.destroyAll();
      }
    } catch (error) {
      console.error('检查锁状态失败:', error);
    }
  }

  // 使用心跳检测Hook
  const { isPolling } = usePollingWithIdleCheck({
    checkLockFn: checkLock,
    idleTimeout: 3000, // 3秒无操作后暂停心跳
    // interval: LockConfig.editLockPollingTime * 1000, // 60秒心跳间隔
    interval: 60 * 1000, // 60秒心跳间隔
    // interval: 10 * 1000, // 60秒心跳间隔
    leading: true, // 不立即执行
  });

  useEffect(() => {
    if (projectId) {
      fetchFolderList();
    }
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      releaseDocumentLock({ requestId: requestId, resourceId: projectId, isEditable })
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }
  }, [projectId]);

  const handleBeforeUnload = () => {
    releaseDocumentLock({ requestId: requestId, resourceId: projectId, isEditable })
  }

  useEffect(() => {
    setStoreState({
      deatailEditabled: editable == '1',
      isEditMode: editable == '1'
    })
  }, [editable])

  useEffect(() => {
    editRef.current = isEditable
  }, [isEditable])


  useEffect(() => {
    // let top = clientTop
    let top = 160
    let res = 800
    if (top > 0 && windowSize?.height) {
      const height = windowSize.height - top - margin - 130
      res = height > 0 ? height : 300
    }
    setSrcollHeight(res)
  }, [clientTop, windowSize?.height])

  // 如果是文档视图，显示文件夹内容表格
  if (currentView === 'document' && currentFolder) {
    return (
      <div className={styles.container}>
        <FolderContentTable
          projectId={projectId!}
          currentFolder={currentFolder}
          onBack={handleBackToFolder}
          onRefreshParent={fetchFolderList}
        />
      </div>
    );
  }

  // 默认显示目录表格
  return (
    <div className={styles.container}>
      {/* 页面标题 */}
      {/* <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
          <FolderOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
          D3221420018_01 西环一二村低压改造工程
        </h2>
      </div> */}

      {/* 操作按钮 */}
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Space wrap>
          {/* <Button
            icon={<ExportOutlined />}
            onClick={handleExport}
            disabled={selectedRowKeys.length === 0}
          >
            导出
          </Button> */}

          <Button
            danger
            icon={<DeleteOutlined />}
            disabled={selectedRowKeys.length === 0 || !deatailEditabled}
            onClick={handleBatchDeleteFolder}
          >
            删除
          </Button>

          {/* 导出 */}
          <Button
            icon={<ExportOutlined />}
            onClick={handleExportSelected}
            disabled={selectedRowKeys.length === 0}
            loading={exportLoading}
          >
            导出
          </Button>


        </Space>
        {/* 审阅通过 */}
        {projectDetail?.docStatus == DocStatusEnum.COMPILING && detailReferrer === 'review' && (
          <Button
            type="primary"
            onClick={handleReviewProject}
          >
            审阅通过
          </Button>
        )}
      </div>

      {/* 目录表格 */}
      <Table
        rowSelection={rowSelection}
        columns={folderColumns}
        dataSource={folderList}
        loading={folderListLoading}
        rowKey="folderId"
        pagination={false}
        scroll={{ x: 'max-content', y: srcollHeight }}
        bordered
        size="small"
      />

      {/* 导出确认弹窗 */}
      <ExportConfirmModal
        visible={exportModalVisible}
        onCancel={handleExportCancel}
        onConfirm={handleExportConfirm}
        loading={exportLoading}
      />
    </div>
  );
};

export default Detail;
